import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { useColorPalette } from '../../contexts/ColorPaletteContext';
import { TV_HEATMAP_COPY } from '../../data/tvDemoDisplay';
import tvTheme from './tvTheme';
import { tvAiryGlass } from './tvBroadcastStyles';

const GRADIENT_SAMPLES = [1, 4, 7, 10, 14, 17, 20];

/** ヒートマップ直前に置く凡例（色＝人の多さを一目で） */
function TVHeatmapGuideStrip({ compact = false, airy = false }) {
  const { getCellColor } = useColorPalette();
  const { scaleTitle, scaleSteps } = TV_HEATMAP_COPY;

  const titleFont = compact ? '0.92rem' : 'clamp(1.1rem, 1.9vw, 1.45rem)';
  const anchorFont = compact ? '0.92rem' : 'clamp(1.2rem, 2.1vw, 1.65rem)';
  const numFont = compact ? '0.85rem' : 'clamp(1.05rem, 1.85vw, 1.45rem)';
  const barH = compact ? 24 : 34;

  const gradient = useMemo(() => {
    const stops = GRADIENT_SAMPLES.map((v, i) => {
      const pct = GRADIENT_SAMPLES.length === 1 ? 0 : (i / (GRADIENT_SAMPLES.length - 1)) * 100;
      return `${getCellColor(v)} ${pct}%`;
    });
    return `linear-gradient(90deg, ${stops.join(', ')})`;
  }, [getCellColor]);

  return (
    <Box
      sx={{
        width: '100%',
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'center',
        px: compact ? 0.65 : 1,
        py: compact ? 0.5 : 0.65,
        bgcolor: airy ? tvAiryGlass.guide : 'rgba(255,255,255,0.96)',
        border: airy ? tvAiryGlass.border : '3px solid #fff',
        borderRadius: 0.5,
        boxShadow: airy ? tvAiryGlass.shadow : '0 2px 10px rgba(13,74,143,0.12)',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: compact ? 0.4 : 0.55,
        }}
      >
        <Typography
          sx={{
            fontSize: titleFont,
            fontWeight: 900,
            color: tvTheme.colors.textDark,
            lineHeight: 1.2,
            textAlign: 'center',
          }}
        >
          {scaleTitle}
        </Typography>

        <Box
          aria-hidden
          sx={{
            height: barH,
            borderRadius: 1,
            background: gradient,
            border: '3px solid #fff',
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.08), 0 2px 8px rgba(13,74,143,0.15)',
          }}
        />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 0.5,
            px: 0.15,
          }}
        >
          {scaleSteps.map((step) => (
            <Box
              key={step.value}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                minWidth: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: anchorFont,
                  fontWeight: 900,
                  color: tvTheme.colors.textDark,
                  lineHeight: 1.15,
                }}
              >
                {step.label}
              </Typography>
              <Typography
                sx={{
                  fontSize: numFont,
                  fontWeight: 900,
                  color: tvTheme.colors.accentBlue,
                  lineHeight: 1.15,
                  mt: 0.15,
                }}
              >
                {step.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

TVHeatmapGuideStrip.propTypes = {
  compact: PropTypes.bool,
  airy: PropTypes.bool,
};

export default TVHeatmapGuideStrip;
